<script setup lang="ts">
import type { NewsThumbFragment, PageThumbFragment, TreatmentThumbFragment } from '#graphql-operations'
import { useForm } from '@formwerk/core'
import { LazySearchNews, LazySearchTreatment } from '#components'

type SearchThumbFragment = NewsThumbFragment | PageThumbFragment | TreatmentThumbFragment

const route = useRoute()
const { locale } = useI18n()
const { getValue } = useForm()

const q = computed(() => getValue('q'))

const { data: search } = await useSearchQuery()

const latestArticlesVariables = computed(() => ({
  limit: 2,
  section: ['news'],
  site: [locale.value],
  orderBy: 'date',
}))
const { data: articles } = await useAsyncGraphqlQuery('Entries', latestArticlesVariables, {
  transform: input => input.data.entries as NewsThumbFragment[],
})

const searchVariables = computed(() => ({
  limit: 6,
  section: ['page', 'news', 'treatment'],
  search: q.value as string,
  site: [locale.value],
  orderBy: 'score',
}))
const { data: results, pending, execute, clear } = await useAsyncGraphqlQuery('Entries', searchVariables, {
  transform: input => input.data.entries as SearchThumbFragment[],
  immediate: false,
})

const sectionHandles = ['page', 'news', 'treatment']

const groupedResults = computed(() => Object.groupBy(results.value ?? [], (entry) => {
  if (entry?.__typename === 'treatmentDefault_Entry')
    return (entry as TreatmentThumbFragment).ancestors[0]?.title ?? entry.sectionHandle ?? ''

  return entry?.sectionHandle ?? ''
}))

const latestArticles = computed(() => (search.value?.chooseArticles ? search.value.articles : articles.value) as SearchThumbFragment[])

const { close } = useMagicDrawer('search-drawer')

watch(route, close)

watch(searchVariables, () => {
  if (((q.value as string)?.length || 0) > 1) {
    execute()
  }
  else {
    clear()
  }
})
</script>

<template>
  <aside v-if="search && !search.disabled" class="searchDrawer contents">
    <MagicDrawerProvider
      id="search-drawer"
      :options="{
        position: 'right',
        scrollLock: {
          padding: false,
        },
        focusTrap: {
          initialFocus: '#search',
        },
      }"
    >
      <MagicDrawerBackdrop />
      <MagicDrawerContent>
        <div class="c-$primary-color bg-white flex flex-col size-full of-auto">
          <div class="container flex shrink-0 h-$header-height items-center justify-between">
            <Text v-if="search" as="h2" class="typo-sans-1 block uppercase" :text="search.title" />
            <ButtonNav :title="$t('close')" @click="close" />
          </div>
          <div class="container space-y-4 lg:space-y-9">
            <form novalidate>
              <SearchField id="search" name="q" :label="$t('search')" />
            </form>
            <section v-if="q" class="space-y-6">
              <template v-if="pending">
                <div v-for="i in 2" :key="i" class="space-y-2">
                  <Skeleton class="h-4 max-w-20" />
                  <ul class="grid space-y-1">
                    <li v-for="n in 3" :key="n">
                      <Skeleton class="h-4 max-w-40" />
                    </li>
                  </ul>
                </div>
              </template>
              <template v-else-if="results && results.length > 0">
                <div v-for="(items, type) in groupedResults" :key="type" class="space-y-2">
                  <template v-if="items?.length">
                    <Text as="h3" class="typo-sans-1 uppercase" :text="sectionHandles.includes(type) ? $t(type, 2) : type" />
                    <ul class="grid space-y-1">
                      <template v-for="entry in items" :key="entry?.slug">
                        <li
                          v-if="entry"
                          :class="{
                            'border-b border-linen last:border-none last:pb-0 pt-1 pb-2': entry.sectionHandle === 'news',
                          }"
                        >
                          <LazySearchNews v-if="entry.sectionHandle === 'news'" :data="(entry as NewsThumbFragment)" />
                          <LazySearchTreatment v-else-if="entry.sectionHandle === 'treatment'" :data="(entry as TreatmentThumbFragment)" />
                          <LazySearchPage v-else-if="entry.sectionHandle === 'page'" :data="(entry as PageThumbFragment)" />
                        </li>
                      </template>
                    </ul>
                  </template>
                </div>
                <div class="text-center pointer-events-auto relative z-1">
                  <ButtonLink :title="$t('view_all_results')" :to="{ name: 'search', query: { q } }" />
                </div>
              </template>
              <p v-else class="typo-sans-1 uppercase">
                {{ $t('there_are_no_entry_to_show', { label: $t('result', 2) }) }}
              </p>
            </section>
            <section v-else class="space-y-6">
              <div class="space-y-2">
                <Text as="h3" class="typo-sans-1 c-$accent-color uppercase" :text="$t('popular_searches')" />
                <ul class="flex gap-1">
                  <template v-for="keyword in search?.keywords" :key="keyword?.string">
                    <li v-if="keyword">
                      <NuxtLinkLocale :to="{ name: 'search', query: { q: keyword.string } }" class="searchPopular group">
                        <Chip :label="keyword.string" class="motion-snug group-hover-media-mouse:bg-linen/60" />
                      </NuxtLinkLocale>
                    </li>
                  </template>
                </ul>
              </div>
              <div class="space-y-2">
                <Text as="h3" class="typo-sans-1 uppercase" :text="$t('latest_articles')" />
                <ul class="grid space-y-1">
                  <template v-for="article in latestArticles" :key="article?.id">
                    <li v-if="article" class="pb-2 pt-1 border-b border-linen last:pb-0 last:border-none">
                      <LazySearchNews :data="(article as NewsThumbFragment)" />
                    </li>
                  </template>
                </ul>
                <div v-if="search && search.links?.length > 0" class="mt-8 flex gap-2 items-center">
                  <ButtonLink v-for="link in search.links" :key="link!.id" :link="link?.customLink" />
                </div>
              </div>
            </section>
          </div>
        </div>
      </MagicDrawerContent>
    </MagicDrawerProvider>
  </aside>
</template>

<style>
.searchDrawer {
  --magic-drawer-height: 100%;
  --magic-drawer-drag-overshoot: 0rem;

  --magic-drawer-enter-animation: slide-rtl-in 0.7s var(--ease-custom-expo);
  --magic-drawer-leave-animation: slide-rtl-out 0.6s var(--ease-custom-expo);

  @screen lg {
    --magic-drawer-drag-overshoot: 70vw;
  }
}
</style>
